import React from 'react'
import { useContext } from 'react'

import classnames from 'classnames'
import { Link, useRouteMatch } from 'react-router-dom'

import { ChatMessagingService } from '../services/chat-messaging-service'
import { Logger } from '../utils/Logger'
import { chatSocket } from '../utils/socket'
import { CurrentUserContext } from './CurrentUserContext'
import { SpecSectionTitle } from './SpecSectionTitle'

export interface InputBoxProps {
  channel: Api_ChannelsDetails.Channel
  onNewMessageSent?: (newMessage: Api_Chat.ChatMessage) => void
}

export const InputBox: React.FC<InputBoxProps> = ({
  channel,
  onNewMessageSent,
}) => {
  const { url } = useRouteMatch()

  const [sending, setSending] = React.useState(false)
  const [messageContent, setMessageContent] = React.useState<string>('')

  const currentUserId = useContext(CurrentUserContext).currentUser?.id

  const canSend = React.useMemo(() => {
    return messageContent.trim().length > 0
  }, [messageContent])

  const onChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessageContent(event.target.value)
    },
    [],
  )

  const send = React.useCallback(async () => {
    if (!currentUserId) {
      return
    }
    setSending(true)
    try {
      const [
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        pendingMessage,
        acknowledgement,
      ] = ChatMessagingService.sendMessage(chatSocket, {
        channelId: channel.id,
        content: messageContent,
        currentUserId,
      })
      const newMessage = await acknowledgement
      setMessageContent('')
      if (newMessage && onNewMessageSent) {
        onNewMessageSent(newMessage)
      }
    } catch (err) {
      //TODO: show error sending
      Logger.error('error sending message', err)
    }
    setSending(false)
  }, [channel.id, currentUserId, messageContent, onNewMessageSent])

  return (
    <>
      <SpecSectionTitle
        title={
          <span>
            Message to {channel.creator.firstName}
            <span className="is-hidden-tablet">
              {' '}
              - <Link to={`${url}/details`}>Show user's details</Link>
            </span>
          </span>
        }
      />
      <div className="pb-4 is-flex is-flex-direction-row is-align-items-center">
        <div className="field is-flex-grow-1 pr-4">
          <div className="control">
            <textarea
              className="textarea"
              placeholder={`Answer to ${channel.creator.firstName}`}
              onChange={onChange}
              value={messageContent}
              disabled={sending}
              rows={2}
            />
          </div>
        </div>
        <div className="control">
          <button
            className={classnames('button', {
              'is-primary': canSend,
              'is-light': !canSend,
              'is-loading': sending,
            })}
            disabled={!canSend || sending}
            onClick={send}>
            <span>Send</span>
            <span className="icon is-small">
              <i className="fas fa-paper-plane" />
            </span>
          </button>
        </div>
      </div>
    </>
  )
}
